import { createQuoteRunActiveError, isQuoteRunJobShown } from '../services/quote-run-open-guard';

export interface AssistantOpenProjectOptions {
    params: {
        projectId?: string;
        targetStage?: number | string;
        intakeJobId?: string;
        jobSnapshot?: any;
    };
    projects: any[];
    handleNavigate: (page: string, options?: any) => void;
    loadProject: (project: any) => Promise<any> | any;
    handleQuoteStageNavigate: (stage: number) => any;
    /**
     * Decision D1: true when a Quote Run refuses the open (it has shown its
     * message). The intake open goes through handleNavigate, which reports
     * nothing, so the bridge asks first.
     */
    refuseProjectChangeDuringQuoteRun?: (change: 'open', projectId?: string) => boolean;
    /** True when the project is the job the Quote Run is driving; it has been shown. */
    showQuoteRunJobInsteadOfOpening?: (projectId?: string) => boolean;
    /**
     * The project load that last painted (App's paintedProjectLoadContextRef).
     * completeProjectLoad replaces it when a project paints, so a plain open
     * that resolved without painting ("Project not found", the unsaved-changes
     * question) moves no stage. Without it the stage move follows any open
     * that resolves, as before 2026-09-29.
     */
    readPaintedProjectLoad?: () => { projectId?: unknown } | null | undefined;
    /**
     * The page on screen now (App's currentPageRef), read whenever it is
     * called. With navigateToQuoteStageLive it makes the stage move wait for
     * QuoteMaker to show the opened project (review 2026-09-30).
     */
    readLivePage?: () => unknown;
    /**
     * App's stage navigation from its latest render, refusing to leave the
     * page on screen (handleQuoteStageNavigateRef with onlyOnQuoteMaker).
     * With readLivePage the stage move goes through it and never through
     * the handleQuoteStageNavigate captured when the tool call started.
     */
    navigateToQuoteStageLive?: (stage: number) => any;
}

// The stage move after an open (review 2026-09-30): its first look, how often
// it looks again while QuoteMaker is not on screen, and how long it waits for
// the operator to reach it.
const STAGE_MOVE_FIRST_LOOK_MS = 350;
const STAGE_MOVE_LOOK_AGAIN_MS = 100;
const STAGE_MOVE_WAIT_MS = 60_000;

export async function handleAssistantOpenProject({
    params = {},
    projects = [],
    handleNavigate,
    loadProject,
    handleQuoteStageNavigate,
    refuseProjectChangeDuringQuoteRun,
    showQuoteRunJobInsteadOfOpening,
    readPaintedProjectLoad,
    readLivePage,
    navigateToQuoteStageLive,
}: AssistantOpenProjectOptions): Promise<{ title: string; quoteRunJob?: true }> {
    const { projectId, targetStage, intakeJobId, jobSnapshot } = params;
    if (!projectId) throw new Error('open_project did not supply a projectId.');
    const summary = projects.find(p => p.id === projectId) || { id: projectId };
    const title = summary.title || summary.quoteTitle || projectId;

    // The Assistant says "I will move to Stage N once QuoteMaker finishes
    // loading it", so the move waits for the project to paint (2026-09-29).
    // It used to fire 350 ms after the open whatever the load did. Off
    // QuoteMaker it navigates there first, so a slow or failed open showed an
    // empty new project (or reopened the tab's other job) and the view-state
    // effect saved that over the tab's record.
    //
    // Review 2026-09-30: the move reads the tab as it is now and never
    // navigates. handleQuoteStageNavigate is the one from the render this
    // tool call started on. Off QuoteMaker it navigated to QuoteMaker itself,
    // from that render's view of the tab (nothing open, Settings on screen).
    // When the open's landing waited behind "Leave with unsaved settings?",
    // that navigation replaced the question, and Discard changes then showed
    // an empty new project, or the tab's older job over the one just opened.
    // Now the move waits until QuoteMaker is on screen showing this project
    // (the landing, or the operator, puts it there), then moves the stage
    // through App's latest render. If another project paints first, or the
    // operator has not reached QuoteMaker within a minute, the project keeps
    // its own stage.
    const moveToTargetStage = () => {
        if (!targetStage) return;
        const stage = Number(targetStage);
        const reportBlocked = (result: any) => {
            if (result?.ok === false) {
                console.warn('[QM Assistant] target stage after open_project blocked:', result.reason);
            }
        };
        if (!readLivePage || !navigateToQuoteStageLive) {
            setTimeout(() => reportBlocked(handleQuoteStageNavigate(stage)), STAGE_MOVE_FIRST_LOOK_MS);
            return;
        }
        let waitedMs = 0;
        const moveOnceQuoteMakerShowsIt = () => {
            if (readPaintedProjectLoad) {
                const painted = readPaintedProjectLoad();
                if (!painted || String(painted.projectId) !== String(projectId)) return;
            }
            if (readLivePage() === 'quotemaker') {
                reportBlocked(navigateToQuoteStageLive(stage));
                return;
            }
            if (waitedMs >= STAGE_MOVE_WAIT_MS) {
                console.info(`[QM Assistant] Stage ${stage} was not applied: QuoteMaker was not on screen within a minute of opening the project.`);
                return;
            }
            waitedMs += STAGE_MOVE_LOOK_AGAIN_MS;
            setTimeout(moveOnceQuoteMakerShowsIt, STAGE_MOVE_LOOK_AGAIN_MS);
        };
        setTimeout(moveOnceQuoteMakerShowsIt, STAGE_MOVE_FIRST_LOOK_MS);
    };

    // A3 review. During a Quote Run the job it is driving is shown, never
    // loaded, and its stage belongs to the run: no stage move follows. Any
    // other open is refused with QUOTE_RUN_ACTIVE, so the Assistant reports
    // the refusal instead of "Opening ..." and no stage move follows either.
    if (intakeJobId && jobSnapshot) {
        if (showQuoteRunJobInsteadOfOpening?.(projectId) === true) return { title, quoteRunJob: true };
        if (refuseProjectChangeDuringQuoteRun?.('open', projectId) === true) {
            throw createQuoteRunActiveError('open', projectId);
        }
        // The routing bridge loads in the background and calls onLanded
        // only once the project has painted.
        handleNavigate('quotemaker', {
            intakeProjectLoad: { projectId, intakeJobId, jobSnapshot, onLanded: moveToTargetStage },
        });
        return { title };
    }
    const paintedBefore = readPaintedProjectLoad?.();
    const opened = await loadProject(summary);
    if (isQuoteRunJobShown(opened)) return { title, quoteRunJob: true };
    if (readPaintedProjectLoad) {
        const painted = readPaintedProjectLoad();
        const landed = !!painted
            && painted !== paintedBefore
            && String(painted.projectId) === String(projectId);
        // loadProject resolved without painting this project: it (or the
        // unsaved-changes question it asked) has spoken. No stage move.
        if (!landed) return { title };
    }
    moveToTargetStage();
    return { title };
}
